import FormCompanyImage from 'components/form/FormCompanyImage';
import FormWysiwygEditor from 'components/form/FormWysiwygEditor';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useState, type JSX } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import type { Path, UseFieldArrayReturn } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import { isEmpty, submit } from 'utils/app';

/**
 * 診断作成編集 診断結果コンポーネント
 */
export default function WsDiagnosisEditResults(props: {
  readOnly: boolean;
  fieldArray: UseFieldArrayReturn<DiagnosisEditFormStateType, 'results', 'id'>;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [acctState, setAcctState] = useAtom(AcctState);

  const [processing, setProcessing] = useState(false);
  const { postDiagnosisAiResults } = useApiState();

  const {
    control,
    getValues,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<DiagnosisEditFormStateType>();
  const { noticeMessageSuccess, getActivePlan } = useAppState();

  const { remove, fields } = props.fieldArray;

  const activePlan = getActivePlan();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */

  /**
   * AIによる結果説明生成を行います。
   */
  const onCreateResultDescription = (resultIndex: number) => {
    submit(processing, setProcessing, async () => {
      try {
        const values = getValues();
        const results = getValues().results;
        let isValid = true;

        clearErrors();

        if (isEmpty(results[resultIndex].title)) {
          setError(`results.${resultIndex}.title` as Path<DiagnosisEditFormStateType>, {
            type: 'required',
          });
          isValid = false;
        }

        if (isEmpty(values.title)) {
          setError('title' as Path<DiagnosisEditFormStateType>, {
            type: 'required',
          });
          isValid = false;
        }

        if (isEmpty(values.description)) {
          setError('description' as Path<DiagnosisEditFormStateType>, {
            type: 'required',
          });
          isValid = false;
        }

        if (!isValid) {
          return;
        }

        const response = await postDiagnosisAiResults({
          companyId: acctState.company.id,
          title: values.title || '',
          description: values.description || '',
          resultTitle: results[resultIndex].title,
        });

        // 先に配列を空にする
        results[resultIndex].descriptionHtml = response.descriptionHtml;

        // 要素の更新
        remove(resultIndex);
        setValue('results', results);

        // AIテキスト生成数の更新
        setAcctState(prev => ({
          ...prev,
          company: {
            ...prev.company,
            aiTextUsageCount: response?.aiTextUsageCount ?? acctState.company.aiTextUsageCount,
          },
        }));

        noticeMessageSuccess('AIによる説明文の生成が完了しました。');
      } catch (e) {
        showBoundary(e);
      }
    });
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * AIテキスト生成数の上限に達しているかどうかを判定します。
   */
  const _isAiTextCountLimit = (): boolean => {
    return acctState.company.aiTextUsageCount >= (activePlan?.aiTextCountLimit ?? 0);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-form-default section-answer-result">
        <div className="container">
          <div className="box-common">
            <h3 className="title-section">診断結果ページ：タイプごとの設定</h3>

            {fields.map((field, resultIndex) => {
              const title = watch(`results.${resultIndex}.title`);
              return (
                <div key={field.id} className="section-body">
                  <div className="form-default-container">
                    <div className="row row-0">
                      <div className="col-md col-header pt-md-0">
                        <div className="form-label mb-lg">
                          診断結果タイプ：
                          {String.fromCharCode(65 + resultIndex)}
                        </div>

                        <div className="card form-header">
                          <h4 className="title">診断結果用のメイン画像</h4>

                          <FormCompanyImage
                            control={control}
                            name={`results.${resultIndex}.imageKey`}
                            readOnly={props.readOnly}
                          />
                        </div>
                      </div>
                      <div className="col-md col-form ps-md-4 ms-md-2">
                        <div className="form-group mb-md-4" style={{ marginTop: '2rem' }}>
                          <label className="form-label">タイプ名</label>
                          <div className="w-100 d-flex">
                            <input
                              className="form-control w-75 me-4"
                              readOnly={true}
                              type={'text'}
                              value={title}
                            />
                            <div className="btn-container max-width-130 w-25 my-auto">
                              <button
                                onClick={() => onCreateResultDescription(resultIndex)}
                                disabled={props.readOnly || _isAiTextCountLimit()}
                                className="btn btn-common ai-generate w-100"
                              >
                                説明の生成
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="form-group">
                          <FormWysiwygEditor
                            control={control}
                            subject="このタイプの説明やフィードバック"
                            name={`results.${resultIndex}.descriptionHtml`}
                            placeholder="すべての回答を終えたユーザーに向けた説明文を入れましょう。画像やリンクなども貼り付けることができます。"
                            toolbar={['image', 'link', 'button']}
                            errors={errors}
                            readOnly={props.readOnly}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
